import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { MessageSquare, Mail, Smartphone, Sparkles } from 'lucide-react';

const data = [
  { channel: 'WhatsApp', paymentRate: 72, deliveryRate: 98, icon: MessageSquare, color: 'hsl(142 70% 45%)' },
  { channel: 'Email', paymentRate: 54, deliveryRate: 95, icon: Mail, color: 'hsl(220 70% 50%)' },
  { channel: 'SMS', paymentRate: 48, deliveryRate: 99, icon: Smartphone, color: 'hsl(280 70% 50%)' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      channel: string;
      paymentRate: number;
      deliveryRate: number;
    };
  }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const entry = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="text-sm font-semibold text-foreground mb-1">{entry.channel}</p>
      <p className="text-xs text-muted-foreground">
        Taxa de Pagamento: <span className="font-bold text-foreground">{entry.paymentRate}%</span>
      </p>
      <p className="text-xs text-muted-foreground">
        Taxa de Entrega: <span className="font-bold text-foreground">{entry.deliveryRate}%</span>
      </p>
    </div>
  );
}

export function ChannelEffectiveness() {
  // Calculate the best channel advantage
  const bestChannel = data.reduce((a, b) => a.paymentRate > b.paymentRate ? a : b);
  const secondBest = data.filter(d => d.channel !== bestChannel.channel)
    .reduce((a, b) => a.paymentRate > b.paymentRate ? a : b);
  const advantage = Math.round(((bestChannel.paymentRate - secondBest.paymentRate) / secondBest.paymentRate) * 100);

  return (
    <div className="p-5 rounded-2xl border bg-card h-full hover:shadow-lg transition-all duration-300">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground">Efetividade por Canal</h3>
        <p className="text-xs text-muted-foreground">Taxa de pagamento por canal de comunicação</p>
      </div>
      
      <div className="h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis 
              type="category" 
              dataKey="channel" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              width={70}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }} />
            <Bar 
              dataKey="paymentRate" 
              radius={[0, 6, 6, 0]}
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Insight card */}
      <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-[hsl(var(--accent-50))] to-[hsl(var(--basic-50))] border border-[hsl(var(--accent-100))]">
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--basic-500))] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-foreground">
            <span className="font-semibold">{bestChannel.channel}</span> tem{' '}
            <span className="font-bold text-[hsl(var(--accent-600))]">{advantage}% mais conversão</span>{' '}
            que {secondBest.channel}
          </p>
        </div>
      </div>
    </div>
  );
}
